import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Avatar, Box, Button, Container, Divider, IconButton, Paper, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Spinner from '../../../components/spinner/spinner';
import { formatDateTime, formatPrice, getAddress, getFullName } from '../../../utils/utils';
import { useSelector } from 'react-redux';
import { USER_ROLES } from '../../../utils/constants';
import StatusPill from '../../../components/statuspill/statuspill.component';
import AdminActions from './components/adminactions.component';
import ExecActions from './components/execactions.component';
import PrescriptionController from './components/prescriptioncontroller.component';
import useFetchRequestDetails from './hooks/useFetchRequestDetails';
import AssignExec from './components/assignexecutive.admin.component';
import GoogleMapComponent from '../../../components/googlemap/GoogleMap.component';
import VendorActions from './components/vendoractions.component';
import ReportItem from '../../../components/reportitem/reportitem.component';
import './myrequestdetails.page.css';

const VendorDetails = ({requestDetails}) => {
  if (!requestDetails) return;

  if (requestDetails.testServiceType === 'Home Collection') return;

  if (!requestDetails.Vendor) {
    return;
  }

  const vendorData = requestDetails?.Vendor?.vendorprofile;
  const vendorPic = vendorData?.profilePic && (
      <Avatar alt="vendor_pic" src={vendorData?.profilePic} />
  );

  return (
    <>
      <Box style={{paddingBlock: '1rem'}}>
        <Typography variant='h6'> Lab Information </Typography>
      </Box>
      <Divider />
      <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 3, sm: 12 }} style={{padding: '2rem'}}>
        <Grid size={{ xs: 12, sm: 4, md: 4 }}>
          <Typography variant="body1">
            Submitted to:
          </Typography>
          <Typography variant="subtitle1" style={{fontWeight: 'bold'}} className='flex-gap'>
            {vendorPic}
            {vendorData?.vendorLegalName}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 4, md: 4 }}>
          <Typography variant="body1">
            Address:
          </Typography>
          <Typography variant="subtitle1" style={{fontWeight: 'bold'}}>
            {getAddress(vendorData)}
          </Typography>
        </Grid>
      </Grid>
    </>
  );
};

VendorDetails.propTypes = {
    requestDetails: PropTypes.object,
};

const FieldExecutiveDetails = ({requestDetails}) => {
  if (!requestDetails) return;

  if (!requestDetails.FieldExecutive) {
    return;
  }

  const fexecData = requestDetails?.FieldExecutive?.fieldexecutive;
  const fexecPic = fexecData?.profilePic && (
      <Avatar alt="fexec_pic" src={fexecData?.profilePic} />
  );
  return (
    <>
      <Box style={{paddingBlock: '1rem'}}>
        <Typography variant='h6'> Field Executive Information </Typography>
      </Box>
      <Divider />
      <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 3, sm: 12 }} style={{padding: '2rem'}}>
        <Grid size={{ xs: 12, sm: 4, md: 4 }}>
          <Typography variant="body1">
            Collected by:
          </Typography>
          <Typography variant="subtitle1" style={{fontWeight: 'bold'}} className='flex-gap'>
            {fexecPic}
            {getFullName(fexecData)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 4, md: 4 }}>
          <Typography variant="body1">
            Mobile:
          </Typography>
          <Typography variant="subtitle1" style={{fontWeight: 'bold'}}>
            {fexecData.mobileNumber}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 4, md: 4 }}>
          <Typography variant="body1">
            Address:
          </Typography>
          <Typography variant="subtitle1" style={{fontWeight: 'bold'}}>
            {getAddress(fexecData)}
          </Typography>
        </Grid>
      </Grid>
    </>
  );
};

FieldExecutiveDetails.propTypes = {
  requestDetails: PropTypes.object,
};

const TestDetails = ({requestDetails}) => {
  if (!requestDetails) return;

  const testItems = () => {
    return requestDetails.requestitems.map((testItem, i) => {
      const testItemData = testItem.testItem;
      return (
        <Fragment key={i}>
          <Grid size={{ xs: 12, sm: 4, md: 4 }}>
            <Typography variant="body1">
              Test Name
            </Typography>
            <Typography variant="subtitle1" style={{fontWeight: 'bold'}}>
              {i + 1}.&nbsp;{testItemData.servicename}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 4 }}>
            <Typography variant="body1">
              Test Description
            </Typography>
            <Typography variant="subtitle1" style={{fontWeight: 'bold'}}>
              {testItemData.description}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 4 }}>
            <Typography variant="body1">
              Price
            </Typography>
            <Typography variant="subtitle1" style={{fontWeight: 'bold'}}>
              {formatPrice(+testItemData.serviceprice)}
            </Typography>
          </Grid>
        </Fragment>
      );
    });
  };

  return (
    <>
      <Box style={{paddingBlock: '1rem'}}>
        <Typography variant='h6'>
            Tests Information
        </Typography>
      </Box>
      <Divider />
      <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 3, sm: 12 }} style={{padding: '2rem'}}>
        {testItems()}
      </Grid>
    </>
  );
};

TestDetails.propTypes = {
  requestDetails: PropTypes.object,
};

const RequestDetails = ({requestDetails}) => {
  if (!requestDetails) return;

  const patientInfo = JSON.parse(requestDetails.patientInfo);
  const prescriptionUI = requestDetails.prescription && (
    <Grid size={{ xs: 12, sm: 4, md: 4 }}>
      <Typography variant="body1">
        Prescritpion
      </Typography>
      <Typography variant="subtitle1" style={{fontWeight: 'bold'}}>
        <PrescriptionController url={requestDetails.prescription} />
      </Typography>
    </Grid>
  );

  const reportsUi = () => {
    if (!requestDetails.reports.length) return;
    const reportsList = requestDetails.reports.map((report, i) => <ReportItem key={i} reportData={report} />);
    return (
      <div style={{padding: '1rem 2rem'}}>
        <Typography variant="subtitle1" style={{fontWeight: 'bold'}}>
          Reports:
        </Typography>
        <div className="request-reports">{reportsList}</div>
        {/* <ul className="responsive-table">
          <li className="table-header">
            <div className="col col-1">Job Id</div>
            <div className="col col-2">Customer Name</div>
            <div className="col col-3">Amount Due</div>
            <div className="col col-4">Payment Status</div>
          </li>
          <li className="table-row">
            <div className="col col-1" data-label="Job Id">42235</div>
            <div className="col col-2" data-label="Customer Name">John Doe</div>
            <div className="col col-3" data-label="Amount">$350</div>
            <div className="col col-4" data-label="Payment Status">Pending</div>
          </li>
          <li className="table-row">
            <div className="col col-1" data-label="Job Id">42442</div>
            <div className="col col-2" data-label="Customer Name">Jennifer Smith</div>
            <div className="col col-3" data-label="Amount">$220</div>
            <div className="col col-4" data-label="Payment Status">Pending</div>
          </li>
          <li className="table-row">
            <div className="col col-1" data-label="Job Id">42257</div>
            <div className="col col-2" data-label="Customer Name">John Smith</div>
            <div className="col col-3" data-label="Amount">$341</div>
            <div className="col col-4" data-label="Payment Status">Pending</div>
          </li>
          <li className="table-row">
            <div className="col col-1" data-label="Job Id">42311</div>
            <div className="col col-2" data-label="Customer Name">John Carpenter</div>
            <div className="col col-3" data-label="Amount">$115</div>
            <div className="col col-4" data-label="Payment Status">Pending</div>
          </li>
        </ul> */}
      </div>
    );
  };

  return (
    <>
      <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 3, sm: 12 }} style={{padding: '2rem'}}>
        <Grid size={{ xs: 12, sm: 4, md: 4 }}>
          <Typography variant="body1">
            Request For
          </Typography>
          <Typography variant="subtitle1" style={{fontWeight: 'bold'}}>
            {requestDetails.requestFor}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 4, md: 4 }}>
          <Typography variant="body1">
            Service Type
          </Typography>
          <Typography variant="subtitle1" style={{fontWeight: 'bold'}}>
            {requestDetails.testServiceType}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 4, md: 4 }}>
          <Typography variant="body1">
            Patient Name
          </Typography>
          <Typography variant="subtitle1" style={{fontWeight: 'bold'}}>
            {patientInfo.patientName}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 4, md: 4 }}>
          <Typography variant="body1">
            Location
          </Typography>
          <Typography variant="subtitle1" style={{fontWeight: 'bold'}}>
            {requestDetails.location}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 4, md: 4 }}>
          <Typography variant="body1">
            Date and Time
          </Typography>
          <Typography variant="subtitle1" style={{fontWeight: 'bold'}}>
            {formatDateTime(requestDetails.testdatetime)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 4, md: 4 }}>
          <Typography variant="body1">
            Patient Number
          </Typography>
          <Typography variant="subtitle1" style={{fontWeight: 'bold'}}>
            {patientInfo.patientNumber}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 4, md: 4 }}>
          <Typography variant="body1">
            Patient Age
          </Typography>
          <Typography variant="subtitle1" style={{fontWeight: 'bold'}}>
            {patientInfo.patientAge}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 4, md: 4 }}>
          <Typography variant="body1">
            Amount
          </Typography>
          <Typography variant="subtitle1" style={{fontWeight: 'bold'}}>
            {formatPrice(requestDetails.amount)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 4, md: 4 }}>
          <Typography variant="body1">
            Status
          </Typography>
          <Typography variant="subtitle1" style={{fontWeight: 'bold'}}>
            <StatusPill status={requestDetails.status} uppercase={true} />
          </Typography>
        </Grid>
        {prescriptionUI}
      </Grid>
      {reportsUi()}
    </>
  );
};

RequestDetails.propTypes = {
  requestDetails: PropTypes.object,
};

const LocationMap = ({requestDetails}) => {
  if (!requestDetails) return;
  const [lng, lat] = requestDetails.coordinate.coordinates;
  return <GoogleMapComponent lat={lat} lng={lng} />;
};

LocationMap.propTypes = {
  requestDetails: PropTypes.object,
};

const MyRequestDetails = () => {
  const userInfo = useSelector(state => state.auth.user);
  const navigate = useNavigate();
  const { requestId } = useParams();
  const { requestDetails, loading, error, setError } = useFetchRequestDetails(requestId);

  const handleBack = () => {
    navigate(-1);
  };

  if (error) {
      const showRequests = () => navigate('/my-requests');
      return (
        <Container className='doc-cat-content'>
          <Typography variant="h6" style={{marginBottom: '1rem'}}>
            Error while fetching request details.
          </Typography>
          <Button  color="error" variant="contained" onClick={showRequests}> Back to Requests </Button>
        </Container>
      );
  }

  if (loading) {
      return (
          <div style={{height: '500px', width: '100%'}}>
              <Spinner inline={false} />
          </div>
      );
  }

  const userRole = userInfo && userInfo.role;
  const requestActions = () => {
    if (!requestDetails) {
      return;
    }

    switch (userRole) {
      case USER_ROLES.SUPER_ADMIN:
        return (
          <div style={{marginLeft: 'auto'}}>
            <AssignExec />
            <AdminActions requestDetails={requestDetails} setError={setError} />
          </div>
        );
      case USER_ROLES.FIELD_EXECUTIVE:
        return (
          <div style={{marginLeft: 'auto'}}>
            <ExecActions requestDetails={requestDetails} />
          </div>
        );
      case USER_ROLES.VENDOR:
        return (
          <div style={{marginLeft: 'auto'}}>
            <VendorActions requestDetails={requestDetails} />
          </div>
        );
      default:
        return;
    }
  };

  const headingUI = userRole === USER_ROLES.PATIENT && (
    <>
      <Box>
          <Typography variant='h6' style={{marginBottom: '1rem'}}>
              Request Details
          </Typography>
      </Box>
      <Divider />
    </>
  );

  return (
    <>
      {headingUI}
      <Paper className='detail-container'>
        <Typography variant='h6' style={{marginBottom: '1rem'}} className='flex-gap'>
            <IconButton aria-label="back" onClick={handleBack}>
                <ArrowBackIcon />
            </IconButton>
            Request Information
            {requestActions()}
        </Typography>
        <Divider />
        <RequestDetails requestDetails={requestDetails} />
        <LocationMap requestDetails={requestDetails} />
        <TestDetails requestDetails={requestDetails} />
        <FieldExecutiveDetails requestDetails={requestDetails} />
        <VendorDetails requestDetails={requestDetails} />
      </Paper>
    </>
  );
};

export default MyRequestDetails;